/**
 * Assembled OpenAPI 3.1 document for the Repackz Game Platform API.
 * Split into modules per layer for maintainability; this file stitches them
 * together into the single object Scalar renders.
 */
import { info } from './info';
import { servers } from './servers';
import { tags, tagGroups } from './tags';
import { securitySchemes } from './security';
import { schemas, parameters } from './components';
import { catalogPaths } from './paths.player.catalog';
import { ordersPaths } from './paths.player.orders';
import { shippingsPaths } from './paths.player.shippings';
import { feedsPaths } from './paths.player.feeds';
import { embedPaths } from './paths.embed';
import { fairnessPaths } from './paths.fairness';
import { operatorPaths } from './paths.operator';
import { shippingPromotionsPaths } from './paths.operator.shippingPromotions';
import { webhooks } from './webhooks';

// The wallet callbacks (we → operator) are modelled as OpenAPI `webhooks`, but
// this Scalar version doesn't render the top-level `webhooks` object — so they
// never appear on the page. Surface them as `paths` (still tagged
// "Wallet Callbacks") so they show in the nav. They target the operator's
// configurable `callback_base_url`, NOT this API's server, so each carries its
// own server block instead of inheriting the document one.
const CALLBACK_SERVERS = [
  {
    url: '{callback_base_url}',
    description:
      'Your wallet callback base URL (set in Integration → Callback Configuration). The per-callback paths below are the defaults and are overridable per operator.',
    variables: { callback_base_url: { default: 'https://your-wallet.example.com' } },
  },
];

// Webhook key (action) → its default callback path.
const CALLBACK_PATHS: Record<string, string> = {
  get_session: '/get_session',
  get_balance: '/get_balance',
  debit: '/debit',
  shipping_debit: '/shipping-debit',
  shipping_settle: '/shipping-settle',
  credit: '/credit',
  refund: '/refund',
};

const callbackPaths = Object.fromEntries(
  Object.entries(webhooks).map(([action, item]) => [
    CALLBACK_PATHS[action] ?? `/${action}`,
    { ...(item as Record<string, unknown>), servers: CALLBACK_SERVERS },
  ]),
);

export const openApiDocument = {
  openapi: '3.1.0',
  info,
  servers,
  tags,
  'x-tagGroups': tagGroups,
  // No document-level default security: each operation declares its own
  // (server-to-server = API key + signature, Player API = session token), so
  // Scalar shows the correct credential per endpoint instead of one globally.
  paths: {
    ...operatorPaths,
    ...shippingPromotionsPaths,
    ...catalogPaths,
    ...ordersPaths,
    ...shippingsPaths,
    ...feedsPaths,
    ...fairnessPaths,
    ...embedPaths,
    ...callbackPaths,
  },
  components: {
    securitySchemes,
    schemas,
    parameters,
  },
} as const;

export type OpenApiDocument = typeof openApiDocument;
